import { isValidDate, replaceStr } from '@utils/shared';

import { CustomStack } from '@components/Styles/CommonStyle';
import ProfileFormTable from '@components/common/Table/ProfileFormTable';
import React from 'react';
import { Typography } from '@mui/material';
import { parseDateInput } from '@utils/formatter';
import { request } from '@api/axios';
import { theme } from '@components/Styles/theme';
import { useDialog } from '@context/DialogContext';
import { useLoading } from '@context/LoadingContext';
import { useNavigate } from 'react-router-dom';

export const setProfileInfo = (data, showDialog) => {
  const { profileData, educationList, workExperienceList, projectHistoryList } = data;
  const validBirthDate = isValidDate(profileData.birthDate);
  if (!validBirthDate) {
    showDialog({ title: '확인', message: '정확한 생년월일을 입력해 주세요.' });
    return;
  }

  let errorMessage = '';

  // 근무 경력 유효성 검사
  workExperienceList.forEach((workExperience, index) => {
    const { year: startYear, month: startMonth } = parseDateInput(workExperience.startDate);
    const { year: endYear, month: endMonth } = parseDateInput(workExperience.endDate);

    // 월 유효성 검사
    if (startMonth && !isValidDate(startMonth, 'MM')) {
      errorMessage = `${index + 1}번째 근무 경력의 시작일자의 형식이 올바르지 않습니다.`;
      return;
    }

    if (endMonth && !isValidDate(endMonth, 'MM')) {
      errorMessage = `${index + 1}번째 근무 경력의 종료일자의 형식이 올바르지 않습니다.`;
      return;
    }

    workExperience.startYear = startYear;
    workExperience.startMonth = startMonth;
    workExperience.endYear = endYear;
    workExperience.endMonth = endMonth;
  });

  // 근무경력에서 유효성 검사 오류가 없으면 프로젝트 경력에서도 유효성 검사 진행
  if (!errorMessage) {
    projectHistoryList.forEach((projectHistory, index) => {
      const { year: startYear, month: startMonth } = parseDateInput(projectHistory.startDate);
      const { year: endYear, month: endMonth } = parseDateInput(projectHistory.endDate);

      if (startMonth && !isValidDate(startMonth, 'MM')) {
        errorMessage = `${index + 1}번째 프로젝트 경력의 시작일자의 형식이 올바르지 않습니다.`;
        return;
      }

      if (endMonth && !isValidDate(endMonth, 'MM')) {
        errorMessage = `${index + 1}번째 프로젝트 경력의 종료일자의 형식이 올바르지 않습니다.`;
        return;
      }

      projectHistory.startYear = startYear;
      projectHistory.startMonth = startMonth;
      projectHistory.endYear = endYear;
      projectHistory.endMonth = endMonth;
    });
  }

  if (errorMessage) {
    showDialog({ title: '확인', message: errorMessage });
    return;
  }

  return { profileData, educationList, workExperienceList, projectHistoryList };
};

export const defaultProfileValues = {
  profileData: {
    name: '',
    birthDate: '',
    gender: '',
    workPeriod: '',
    grade: '',
    license: '',
    phone: '',
    email: '',
    position: '',
    workType: '',
    address: '',
  },
  educationList: Array.from({ length: 3 }, () => ({
    schoolName: '',
    major: '',
    educationStatus: '',
    graduationYear: '',
    graduationMonth: '',
  })),
  workExperienceList: [{ companyName: '', startDate: '', endDate: '', position: '', duty: '' }],
  projectHistoryList: [
    { projectName: '', startDate: '', endDate: '', clientCompany: '', employerCompany: '', duty: '', skill: '' },
  ],
};

const ProfileCreate = () => {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const { showDialog } = useDialog();

  const handleProfileRegister = async (data) => {
    showLoading();
    try {
      const payload = setProfileInfo(data, showDialog);

      if (payload) {
        const { response, status, message } = await request('post', '/api/profile/register', payload);

        if (status === 201) {
          showDialog({
            title: '확인',
            message: '등록되었습니다.',
            showCloseButton: false,
            onConfirm: () => {
              navigate('/profile/list');
            },
          });
        } else {
          throw new Error(message);
        }
      }
    } catch (error) {
      console.log(error);
      showDialog({ title: '확인', message: '등록에 실패했습니다.\n잠시 후 다시 시도해주세요.' });
    } finally {
      hideLoading();
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <React.Fragment>
      <CustomStack mb={2}>
        <Typography sx={theme.typography.h4}>프로필 등록</Typography>
      </CustomStack>
      <ProfileFormTable initialData={defaultProfileValues} onSubmit={handleProfileRegister} onCancel={handleCancel} />
    </React.Fragment>
  );
};

export default ProfileCreate;
